define((require) => {
    "use strict";

    const Permissions                     = require("shared/security/Permissions");
    const SettingsView                    = require("ui/settings/SettingsView");
    const {isBlank}                       = require("shared/util/StringUtils");
    const template                        = require("ui/settings/export/ExportView.tpl");
    const Promise                         = require("shared/util/Promise");
    const ExperienceModel                 = require("shared/ui/experience/ExperienceModel");
    const BreakpointGroup                 = require("shared/ui/breakpoint/BreakpointGroup");
    const BreakpointExportModel           = require("shared/ui/breakpoint/BreakpointExportModel");
    const ViewScope                       = require("shared/ui/common/ViewScope");
    const TemplateItemViewController      = require("ui/dashboard/TemplateItemViewController");
    const MD5                             = require("md5");
    const ValidateBehavior                = require("ui/behaviors/ValidateBehavior");
    const LoggedInUserState               = require("shared/ui/user/LoggedInUserState");
    const NotificationBar                 = require("editor/ui/notificationbar/NotificationBar");
    const ExportUtilities                 = require("editor/ui/settings/export/ExportUtilities").default;

    return SettingsView.extend({
        className: SettingsView.prototype.className + " export-view",
        controller: TemplateItemViewController,
        template: template,

        templateHelpers: function() {
            ViewScope.register(this, this);
            return {
                permissionToManage: Permissions.Privileges.ADMIN.can()
            };
        },

        ui: {
            tabMenuItem:             ".tab",
            experienceExport:        ".export-io",
            experienceExportApiKey:  ".export-key",
            experienceGroupExportApiKey:  ".export-group-key",
            exportAction:            ".btn-export-action"
        },

        bindings: {
            ".export-key": "experienceExportApiKey",
            ".export-group-key": "experienceGroupExportApiKey",
            ".notification-box": {
                observe: ["experienceExportApiKey"],
                updateView: false,
                visible: function(values) {
                    return values[0] && values[0].length >= 0 && values[1] && values[1].length >= 0;
                }
            }
        },

        events: {
            "click @ui.exportAction": "_exportActionClickHandler"
        },

        behaviors: {
            validateBehavior: {
                behaviorClass: ValidateBehavior,
                inputValidation: {
                    ".export-key": {
                        validateAfter: "beforeSubmit",
                        model:     true,    // mark as bound to model, so make sure we cancel pending save requests on error!
                        validator: function(key) {
                            return isBlank(key) || /^\w{24,24}$/.test(key); // at least 24 word characters (upper/lowercase letters + digits)
                        },
                        errorText: function() {
                            return "Not a valid Experience Id";
                        }
                    }
                }
            }
        },
        _exportActionClickHandler(event) {
            ViewScope.register(this, this);
            event.preventDefault();
            this.exportExperience();
        },
        /**
         * Function Exporting an experience, only available to SiteManager role
         *  We are adding an exportedFromExperienceId OR exportedFromExperienceGroupId
         *      property here so you know what experience/group was the source of each new object.
         *      the exportedFromExperienceHash/exportedExperienceGroupHash is meant to be a transactionID for debugging
         *      If you export an experience 5 times to an account throughout time, and you want to know which
         *      of the individual experienceIds go with each of the export attempts (yeah I know the experiences
         *      contain the groupId so this may be overkill), but you can then look at this Hash value
         *      and always are able to tie the experienceGroup and its children experienceIds together
         *      as an audit, just in case something goes wrong, like an export shits out halfway and you're left with
         *      experiences hanging around and you don't know where they came from.
         *      It doesn't act like an unique key in MongoDB, its more for auditing and no code actually ever uses it.
         */
        exportExperience: function() {
            const experienceId = this.options.model.get("experienceExportApiKey");
            if (experienceId) {
                try {
                    const duplicateHash = MD5((+new Date()).toString(36), experienceId);
                    new ExperienceModel({id: experienceId}).fetch().done((model) => {  // this.model has the data after I fetch it
                        const experienceModel = new ExperienceModel(model.toJSON(null, ["companyId"]));
                        experienceModel.set("name", experienceModel.getDisplayName() + " (exported)");
                        experienceModel.set("exportedFromExperienceId", experienceModel.get("id"));
                        experienceModel.set("exportedExperienceHash", duplicateHash);
                        const imageIds = ExportUtilities.getImageIds(experienceModel);
                        if (imageIds !== null && imageIds.length !== null && imageIds.length > 0) {
                            return ExportUtilities
                                .getImageIdsMap(imageIds)
                                .then((imageIdsMap) => {
                                    const modelWithReplacements = ExportUtilities
                                        .replaceImageIdsGivenMap(experienceModel, imageIdsMap);
                                    this._duplicate(modelWithReplacements);
                                });
                        }
                        return this._duplicate(experienceModel);
                    });
                } catch (e) {
                    alert("EXPERIENCE ID IS NOT VALID!!:" + experienceId);
                }
            } else {
                const experienceGroupId = this.options.model.get("experienceGroupExportApiKey");
                /* Ok so duplicate Groups here is interesting, you can't take single experiences approach
                when you get a BreakPoint Group object it does NOT have scenes, so you can't expect to:
                    get the Breakpoints
                    loop through them
                    get all the scenes
                    replace the ids
                    save the object
                 Instead what you need to do is
                    clone the Breakpoint Group as is
                    Loop through all the experiences just made
                    then swap all the images links out in those experience directly.
                    this happens downstream AFTER the new experiences are made in DuplicateGroupAction.js
                 */
                const self = this;
                const grouplicateHash = MD5((+new Date()).toString(36), experienceGroupId);
                new BreakpointExportModel({id: experienceGroupId}).fetch().done((model) => {
                    const breakpointGroupModel = new BreakpointGroup(model.toJSON(null, ["companyId"]));
                    // later code in DuplicateGroupAction.js needs this id!!
                    breakpointGroupModel.set("exportedFromExperienceGroupId", breakpointGroupModel.get("id"));
                    breakpointGroupModel.set("exportedExperienceGroupHash", grouplicateHash);
                    self._fetchAssets(breakpointGroupModel).then(() => {
                        self._duplicate(breakpointGroupModel);
                    });
                });
            }
        },
        onRender: function() {
            const settings = LoggedInUserState.get("companySettings");
            if (settings.get("exportingExperiencesEnabled")) {
                this._defaultSelection();
            } else {
                NotificationBar.error(`Invalid Page`);
            }
        },
        _duplicate: function(experienceModel) {
            return this.trigger("duplicate", experienceModel, "ExperienceExport");
        },
        /**
         * @private
         * Activates the Export tab
         */
        _defaultSelection: function() {
            this._selectTab("experienceExport");
        },

        _fetchAssets(groupModel) {
            const promises = [];
            groupModel.get("entries").models.forEach((experienceModel)  => {
                promises.push(experienceModel.get("asset").fetch());
            });
            return Promise.all(promises);
        },

        /**
         * @private
         * Toggles active class on tabs and content.
         *
         * @param {String} tabName The tabName that should be activated.
         */
        _selectTab: function(tabName) {
            this.ui.experienceExport.toggleClass("active", tabName === "experienceExport");
        }

    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/settings/export/ExportView.js